import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ConsultationApi from "../../../../../infrastructure/api/consultation";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";
import EnhancedTable from "../../../../components/shared/table";
import { alpha } from "@mui/material/styles";

const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return null;
    return u.id ?? u.userId ?? u.UserId ?? null;
  } catch {
    return null;
  }
};

const normalizeConsultationRow = (raw) => {
  const tags = raw?.tagTitles ?? raw?.TagTitles ?? [];
  return {
    ...raw,
    id: raw?.id ?? raw?.Id,
    mobile: raw?.mobile ?? raw?.Mobile ?? "",
    fullName: raw?.fullName ?? raw?.FullName ?? "",
    statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
    tagTitlesDisplay: Array.isArray(tags) ? tags.join("، ") : String(tags || ""),
    assignedExpertName:
      raw?.assignedExpertName ?? raw?.AssignedExpertName ?? "",
    createOn: raw?.createOn ?? raw?.CreateOn ?? null,
    nextFollowUpAt: raw?.nextFollowUpAt ?? raw?.NextFollowUpAt ?? null,
  };
};

const ConsultationRequestsPage = ({
  api = ConsultationApi,
  operatorMode = false,
}) => {
  const operatorFilterInitDone = useRef(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [filterTick, setFilterTick] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [tags, setTags] = useState([]);
  const [experts, setExperts] = useState([]);
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({
    statusId: "",
    tagId: "",
    expertUserId: "",
    needsFollowUp: "",
    search: "",
    fromDate: null,
    toDate: null,
  });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!operatorMode || operatorFilterInitDone.current) return;
    const uid = getCurrentUserId();
    if (uid == null) return;
    operatorFilterInitDone.current = true;
    setFilters((f) => ({ ...f, expertUserId: String(uid) }));
    setFilterTick((x) => x + 1);
  }, [operatorMode]);

  const loadMeta = useCallback(async () => {
    try {
      const [st, tg, ex] = await Promise.all([
        api.getStatuses(),
        api.getTags(),
        api.getExperts(),
      ]);
      if (st?.isSuccess) setStatuses(st.data || []);
      if (tg?.isSuccess) setTags(tg.data || []);
      if (ex?.isSuccess) setExperts(ex.data || []);
      if (!operatorMode) {
        const rp = await api.getReport();
        if (rp?.isSuccess) setReport(rp.data);
        else setReport(null);
      } else {
        setReport(null);
      }
    } catch {
      /* ignore */
    }
  }, [api, operatorMode]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const f = filtersRef.current;
      const params = {
        page: page + 1,
        pageSize,
        search: f.search || undefined,
      };
      if (f.statusId) params.statusId = f.statusId;
      if (f.tagId) params.tagId = f.tagId;
      if (f.expertUserId) params.expertUserId = f.expertUserId;
      if (f.needsFollowUp === "1") params.needsFollowUp = true;
      if (f.fromDate) params.fromDate = f.fromDate;
      if (f.toDate) params.toDate = f.toDate;
      const res = await api.getPagedList(params);
      if (res?.isSuccess && res.data) {
        const items = res.data.items || [];
        setRows(items.map(normalizeConsultationRow));
        setTotal(res.data.total || 0);
      } else {
        toast.error(res?.message || "خطا در دریافت لیست");
      }
    } catch {
      toast.error("خطا در دریافت لیست");
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, filterTick]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = useCallback(async (id) => {
    try {
      const res = await api.getDetail(id);
      if (res?.isSuccess) {
        setDetail(res.data);
        setDetailOpen(true);
      } else toast.error(res?.message || "خطا");
    } catch {
      toast.error("خطا در دریافت جزئیات");
    }
  }, [api]);

  const headCells = useMemo(
    () => [
      { id: "mobile", label: "موبایل" },
      { id: "fullName", label: "نام" },
      { id: "statusTitle", label: "وضعیت" },
      { id: "tagTitlesDisplay", label: "تگ‌ها" },
      { id: "assignedExpertName", label: "کارشناس" },
      { id: "createOn", label: "ثبت", type: "persiandatetime" },
      { id: "nextFollowUpAt", label: "پیگیری بعدی", type: "persiandatetime" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <VisibilityIcon fontSize="small" />,
        onClick: (row) => openDetail(row.id),
        color: "primary",
        tooltip: "جزئیات",
      },
    ],
    [openDetail]
  );

  const saveHeader = async (patch) => {
    if (!detail) return;
    const res = await api.updateRequest({ id: detail.id, ...patch });
    if (res?.isSuccess) {
      await openDetail(detail.id);
      loadList();
    } else toast.error(res?.message || "خطا");
  };

  const addFollow = async (payload) => {
    if (!detail) return;
    const res = await api.addFollowUp({
      consultationRequestId: detail.id,
      ...payload,
    });
    if (res?.isSuccess) {
      toast.success("ثبت شد");
      openDetail(detail.id);
      loadList();
    } else toast.error(res?.message || "خطا");
  };

  const saveTags = async (tagIds) => {
    if (!detail) return;
    const res = await api.setTags({
      consultationRequestId: detail.id,
      tagIds,
    });
    if (res?.isSuccess) {
      await openDetail(detail.id);
      loadList();
    } else toast.error(res?.message || "خطا");
  };

  const onConvert = async () => {
    if (!detail || operatorMode) return;
    const res = await api.convertToUser(detail.id);
    if (res?.isSuccess) {
      toast.success(res.data?.message || res.message || "انجام شد");
      openDetail(detail.id);
      loadList();
    } else toast.error(res?.message || "خطا");
  };

  const downloadCsv = async () => {
    if (operatorMode) return;
    const f = filtersRef.current;
    const params = {};
    if (f.statusId) params.statusId = f.statusId;
    if (f.tagId) params.tagId = f.tagId;
    if (f.expertUserId) params.expertUserId = f.expertUserId;
    if (f.needsFollowUp === "1") params.needsFollowUp = true;
    if (f.fromDate) params.fromDate = f.fromDate;
    if (f.toDate) params.toDate = f.toDate;
    if (f.search) params.search = f.search;
    try {
      const res = await api.exportCsv(params);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `consultation-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("خطا در خروجی");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        درخواست‌های مشاوره
      </Typography>
      {operatorMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          به‌صورت پیش‌فرض درخواست‌های ارجاع‌شده به شما نمایش داده می‌شود؛ با فیلتر «کارشناس» می‌توانید همه را ببینید.
        </Typography>
      )}
      {report && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">خلاصه گزارش</Typography>
          <Typography variant="body2">
            نیازمند پیگیری: {report.needsFollowUpCount} | منجر به ثبت‌نام:{" "}
            {report.registeredUserCount}
          </Typography>
        </Paper>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 2,
          }}
        >
          <TextField
            label="جستجو"
            size="small"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
          <FormControl size="small">
            <InputLabel>وضعیت</InputLabel>
            <Select
              label="وضعیت"
              value={filters.statusId}
              onChange={(e) => setFilters((f) => ({ ...f, statusId: e.target.value }))}
            >
              <MenuItem value="">همه</MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>تگ</InputLabel>
            <Select
              label="تگ"
              value={filters.tagId}
              onChange={(e) => setFilters((f) => ({ ...f, tagId: e.target.value }))}
            >
              <MenuItem value="">همه</MenuItem>
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>کارشناس</InputLabel>
            <Select
              label="کارشناس"
              value={filters.expertUserId}
              onChange={(e) =>
                setFilters((f) => ({ ...f, expertUserId: e.target.value }))
              }
            >
              <MenuItem value="">همه</MenuItem>
              {experts.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.fullName || u.FullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>نیاز پیگیری</InputLabel>
            <Select
              label="نیاز پیگیری"
              value={filters.needsFollowUp}
              onChange={(e) =>
                setFilters((f) => ({ ...f, needsFollowUp: e.target.value }))
              }
            >
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="1">بله</MenuItem>
            </Select>
          </FormControl>
          <JalaliDatePicker
            label="از تاریخ"
            fullWidth
            size="small"
            value={filters.fromDate ? new Date(filters.fromDate) : null}
            onChange={(value) =>
              setFilters((f) => ({ ...f, fromDate: value || null }))
            }
          />
          <JalaliDatePicker
            label="تا تاریخ"
            fullWidth
            size="small"
            value={filters.toDate ? new Date(filters.toDate) : null}
            onChange={(value) =>
              setFilters((f) => ({ ...f, toDate: value || null }))
            }
          />
          <Button
            variant="outlined"
            onClick={() => {
              setPage(0);
              setFilterTick((x) => x + 1);
            }}
          >
            اعمال فیلتر
          </Button>
          {!operatorMode && (
            <Button variant="outlined" onClick={downloadCsv}>
              خروجی CSV
            </Button>
          )}
        </Box>
      </Paper>

      <EnhancedTable
        rowKey="id"
        rows={rows}
        headCells={headCells}
        actions={tableActions}
        actionsMode="menu"
        loading={loading}
        showFilter={false}
        pageSize={pageSize}
        serverPagination={{
          totalCount: total,
          page,
          rowsPerPage: pageSize,
          onPageChange: (newPage) => setPage(newPage),
          onRowsPerPageChange: (next) => {
            setPageSize(next);
            setPage(0);
          },
        }}
      />

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            width: "100%",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "unset", flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="h6" component="span">
              جزئیات درخواست مشاوره
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDetailOpen(false)}
              aria-label="بستن"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            flex: "1 1 auto",
            overflowY: "auto",
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          {detail && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    borderInlineStart: `4px solid ${theme.palette.primary.main}`,
                  })}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <PhoneIcon color="primary" sx={{ fontSize: 32, mt: 0.25 }} />
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                            شماره موبایل
                          </Typography>
                          <Typography
                            variant="h5"
                            component="p"
                            sx={{
                              fontWeight: 800,
                              letterSpacing: 0.5,
                              direction: "ltr",
                              unicodeBidi: "embed",
                              mt: 0.25,
                            }}
                          >
                            {detail.mobile}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <PersonIcon color="primary" sx={{ fontSize: 32, mt: 0.25 }} />
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                            نام متقاضی
                          </Typography>
                          <Typography variant="h6" component="p" sx={{ fontWeight: 700, mt: 0.25 }}>
                            {detail.fullName?.trim() ? detail.fullName : "— ثبت نشده —"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    متن درخواست
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {detail.message?.trim() ? detail.message : "—"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>وضعیت</InputLabel>
                  <Select
                    label="وضعیت"
                    value={detail.statusId}
                    onChange={(e) => saveHeader({ statusId: Number(e.target.value) })}
                  >
                    {statuses.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>کارشناس</InputLabel>
                  <Select
                    label="کارشناس"
                    value={detail.assignedExpertUserId || ""}
                    onChange={(e) =>
                      saveHeader({
                        assignedExpertUserId: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  >
                    <MenuItem value="">—</MenuItem>
                    {experts.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.fullName || u.FullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!detail.needsFollowUp}
                      onChange={(e) =>
                        saveHeader({ needsFollowUp: e.target.checked })
                      }
                    />
                  }
                  label="نیاز به پیگیری"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JalaliDateTimePicker
                  label="تاریخ پیگیری بعدی"
                  fullWidth
                  size="small"
                  value={
                    detail.nextFollowUpAt
                      ? new Date(detail.nextFollowUpAt)
                      : null
                  }
                  onChange={(d) =>
                    saveHeader({
                      nextFollowUpAt: d ? d.toISOString() : null,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">تگ‌ها</Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  {tags.map((t) => {
                    const checked = (detail.tagIds || []).includes(t.id);
                    return (
                      <Grid item xs={6} sm={4} md={3} key={t.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={() => {
                                const set = new Set(detail.tagIds || []);
                                if (set.has(t.id)) set.delete(t.id);
                                else set.add(t.id);
                                saveTags([...set]);
                              }}
                            />
                          }
                          label={t.title}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">ثبت تماس / یادداشت</Typography>
              </Grid>
              <Grid item xs={12}>
                <FollowUpForm
                  detail={detail}
                  allowConvertToUser={!operatorMode}
                  onConvert={onConvert}
                  onSubmit={(data) =>
                    addFollow({
                      entryKind: data.kind,
                      callResult: data.callResult,
                      description: data.description,
                      needsFollowUp: data.needsFollowUp,
                      nextFollowUpAt: data.nextFollowUpAt || null,
                    })
                  }
                />
              </Grid>
              {detail.convertedUserId && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <Button
                      component={Link}
                      to={`/SLN/Management/CustomerInfo/${detail.convertedUserId}`}
                      variant="outlined"
                      size="medium"
                    >
                      مشاهده پروفایل مشتری ({detail.convertedUserId})
                    </Button>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2">تاریخچه</Typography>
              </Grid>
              {[...(detail.followUps || [])]
                .sort((a, b) => {
                  const ta = a.createOn ? new Date(a.createOn).getTime() : 0;
                  const tb = b.createOn ? new Date(b.createOn).getTime() : 0;
                  if (tb !== ta) return tb - ta;
                  return (b.id || 0) - (a.id || 0);
                })
                .map((f) => (
                  <Grid item xs={12} key={f.id}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption">
                        {f.createOn ? new Date(f.createOn).toLocaleString("fa-IR") : ""}{" "}
                        — {f.expertName} —{" "}
                        {f.entryKind === 1 ? "یادداشت داخلی" : "تماس"}
                      </Typography>
                      <Typography variant="body2">{f.description}</Typography>
                      {f.callResult && (
                        <Typography variant="body2">نتیجه: {f.callResult}</Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2, flexShrink: 0 }}>
          <Button onClick={() => setDetailOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FollowUpForm = ({ detail, onSubmit, onConvert, allowConvertToUser = true }) => {
  const [kind, setKind] = useState(0);
  const [callResult, setCallResult] = useState("");
  const [description, setDescription] = useState("");

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={kind === 0 ? 6 : 12}>
        <FormControl size="small" fullWidth>
          <InputLabel>نوع</InputLabel>
          <Select
            label="نوع"
            value={kind}
            onChange={(e) => setKind(Number(e.target.value))}
          >
            <MenuItem value={0}>تماس</MenuItem>
            <MenuItem value={1}>یادداشت داخلی</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {kind === 0 && (
        <Grid item xs={12} sm={6}>
          <TextField
            size="small"
            fullWidth
            label="نتیجه تماس"
            value={callResult}
            onChange={(e) => setCallResult(e.target.value)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="توضیحات"
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={detail.convertedUserId || !allowConvertToUser ? 12 : 6}>
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              onClick={() => {
                if (!description.trim()) {
                  toast.error("توضیحات را وارد کنید");
                  return;
                }
                const isCall = kind === 0;
                onSubmit({
                  kind,
                  callResult,
                  description,
                  needsFollowUp: isCall ? !!detail.needsFollowUp : false,
                  nextFollowUpAt: isCall ? detail.nextFollowUpAt || null : null,
                });
                setDescription("");
                setCallResult("");
              }}
            >
              ثبت
            </Button>
          </Grid>
          {!detail.convertedUserId && allowConvertToUser && (
            <Grid item xs={12} sm={6}>
              <Button
                color="secondary"
                variant="contained"
                size="medium"
                fullWidth
                onClick={onConvert}
              >
                ایجاد حساب کاربری / لینک به کاربر موجود
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ConsultationRequestsPage;
